import { useEffect, useState, memo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DigitalmarketingIcon,
  Advertisingicon,
  Graphicdesignicon,
  Webdevelopmenticon,
  Mobileappicon,
  Brandingicon,
} from "../../assets/data/icon_data";

import {
  faAnchor,
  faPhone,
  faEnvelope,
  faChevronDown,
  faChevronUp,
  faTimes,
  faShip,
} from "@fortawesome/free-solid-svg-icons";
import NavbarLogo from "../../assets/companylogo_Alter1.svg";


import { GlobalData } from "../../assets/data/GlodalData";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Reusable DropdownMenu component
const DropdownMenu = memo(
  ({ isOpen, links, onMouseEnter, onMouseLeave, closeAllMenus }) => {
    if (!isOpen) return null;

    // Determine column layout based on number of links
    const numColumns = Math.min(
      links.length > 15 ? 5 : links.length > 10 ? 4 : links.length > 5 ? 3 : 2,
      5,
    );
    const linksPerColumn = Math.ceil(links.length / numColumns);
    const columns = Array.from({ length: numColumns }, (_, i) =>
      links.slice(i * linksPerColumn, (i + 1) * linksPerColumn),
    );

    return (
      <div
        className="origin-top-right fixed mt-2 right-0 w-screen rounded-md bg-white/95 divide-y divide-gray-400 z-50 border-t-4 border-yellow-500 shadow-2xl"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="flex container mx-auto">
          {columns.map((columnLinks, colIndex) => (
            <div key={colIndex} className={`w-1/${numColumns} p-6`}>
              {columnLinks.map((link) => (
                <a href={link.path} key={link.path}>
                  <div className="block px-4 py-3 text-[14px] text-gray-300 hover:bg-yellow-500/10 hover:text-yellow-400 transition-colors duration-200 rounded-lg mb-1">
                    <div className="flex items-start">
                      <FontAwesomeIcon
                        className="mt-1 mr-3 text-lg"
                        icon={link.icon}
                      />
                      <div>
                        <span className="font-semibold block mb-0.5">
                          {link.title}
                        </span>
                        <p className="text-[10px] text-gray-500 leading-tight">
                          {link.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  },
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    company: false,
    services: false,
    operations: false,
    contact: false,
    advertising: false,
    more: false,
    mobileMenu: false,
  });
  const [isOpen, setIsOpen] = useState(false); // For hamburger menu (both mobile & desktop)
  const [openMobileSubmenus, setOpenMobileSubmenus] = useState({});
  const [closeTimeouts, setCloseTimeouts] = useState({}); // Per-menu timeouts
  const [isLargeScreen, setIsLargeScreen] = useState(false); // Detect screen size for styling

  const closeAllMenus = () => {
    setOpenMenus({
      company: false,
      services: false,
      operations: false,
      contact: false,
      advertising: false,
      more: false,
      mobileMenu: false,
    });
  };

  const handleMouseEnter = (menuKey, hasLinks) => {
    if (!hasLinks) return; // ⛔ Don't open dropdown if no links

    if (closeTimeouts[menuKey]) {
      clearTimeout(closeTimeouts[menuKey]);
      setCloseTimeouts((prev) => ({ ...prev, [menuKey]: null }));
    }
    closeAllMenus();
    setOpenMenus((prev) => ({ ...prev, [menuKey]: true }));
  };

  const handleMouseLeave = (menuKey, hasLinks) => {
    if (!hasLinks) return;

    const timeout = setTimeout(() => {
      setOpenMenus((prev) => ({ ...prev, [menuKey]: false }));
    }, 300);

    setCloseTimeouts((prev) => ({ ...prev, [menuKey]: timeout }));
  };

  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    const handleScrollClose = () => {
      setIsOpen(false);
    };

    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    if (isOpen) {
      // Small delay to prevent immediate closing on the click that opens it
      const timer = setTimeout(() => {
        window.addEventListener("scroll", handleScrollClose);
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
      }, 10);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("scroll", handleScrollClose);
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    const checkScreenSize = () => {
      const largeScreen = window.innerWidth >= 1024; // lg breakpoint
      setIsLargeScreen(largeScreen);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const NavImages = {
    link1: Webdevelopmenticon,
    link2: DigitalmarketingIcon,
    link3: Brandingicon,
    link4: Advertisingicon,
    link5: Mobileappicon,
    link6: Graphicdesignicon,
  };

  const menuConfigs = [
    {
      key: "company",
      title: "COMPANY",
      href: "/aboutus",
      links: [],
      icon: faAnchor,
      bgClass: "bg-[#F06C60] hover:bg-[#D65A4E] text-white",
    },
    {
      key: "services",
      title: "SERVICES",
      href: "/services",
      links: [],
      icon: faShip,
      bgClass: "bg-[#F4A261] hover:bg-[#DE8E4D] text-white",
    },
    {
      key: "operations",
      title: "OPERATIONS",
      href: "/operations",
      links: [],
      icon: faAnchor, // Using faAnchor as well, you can change to another if preferred
      bgClass: "bg-[#C85D53] hover:bg-[#B04B41] text-white",
    },
    {
      key: "contact",
      title: "CONTACT",
      href: "/contactus",
      links: [],
      icon: faPhone,
      bgClass: "bg-[#9176AC] hover:bg-[#7D6298] text-white",
    },
  ];

  const toggleMobileSubmenu = (key) => {
    setOpenMobileSubmenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setOpenMobileSubmenus({});
  };

  return (
    <>
      <section
        className={`fixed w-full top-0 z-[2000] transition-all duration-500 ${scrolled ? "bg-black/60 backdrop-blur-md shadow-2xl" : "bg-transparent py-4"
          }`}
      >
        <div className="px-2 text-[14px] justify-between flex items-center">
          <a href="/" className="z-10">
            <img
              className="p-2 h-auto w-[160px] lg:w-[180px] transition-all"
              src={NavbarLogo}
              alt="csd_logo"
            />
          </a>

          {/* Hamburger Button - Always visible on all screens */}
          <div className="p-2 z-[2001]">
            <button
              className="text-black hover:text-yellow-500 transition-colors p-2"
              onClick={() => setIsOpen(!isOpen)}
            >
              <FontAwesomeIcon
                icon={isOpen ? faTimes : faAnchor}
                className="text-2xl lg:text-3xl text-white"
              />
            </button>
          </div>
        </div>
      </section>

      {/* Hamburger Menu - Shows on both mobile and desktop */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 bottom-0 z-[5000] flex flex-col transition-all duration-500 ease-in-out h-screen ${isOpen ? "translate-x-0" : "translate-x-full"
          } ${isLargeScreen
            ? "w-[400px]" // Desktop: Slide-in from right, fixed width
            : "w-[85vw]" // Mobile: 85% width to allow clicking outside
          }`}
      >
        {/* Menu Content - Same for both mobile and desktop */}
        <div className="flex-1 bg-white overflow-y-auto overflow-x-hidden p-8 pb-32 relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-32 -mt-32" />
          {/* Menu Header */}
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
            <a href="/" onClick={handleLinkClick}>
              <img className="h-8 w-auto" src={NavbarLogo} alt="csd_logo" />
            </a>
          </div>

          {/* Menu Items */}
          <div className="flex flex-col space-y-4 text-black relative z-10">
            {menuConfigs.map((menu, i) => (
              <div key={i}>
                {menu.links.length > 0 ? (
                  <>
                    <button
                      onClick={() => toggleMobileSubmenu(menu.key)}
                      className="w-full flex justify-between items-center py-4 text-left text-lg font-medium hover:text-yellow-500 transition-colors group"
                    >
                      <div className="flex items-center gap-4">
                        <span className="w-8 flex justify-center text-primary group-hover:scale-110 transition-transform">
                          <FontAwesomeIcon icon={menu.icon} />
                        </span>
                        <span>{menu.title}</span>
                      </div>
                      <FontAwesomeIcon
                        icon={
                          openMobileSubmenus[menu.key]
                            ? faChevronUp
                            : faChevronDown
                        }
                        className={`text-sm text-gray-500 transition-transform duration-300 ${openMobileSubmenus[menu.key] ? "rotate-180" : ""
                          }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${openMobileSubmenus[menu.key]
                        ? "max-h-[1000px] opacity-100 mb-4"
                        : "max-h-0 opacity-0"
                        }`}
                    >
                      <div className="ml-12 grid grid-cols-1 gap-2 pl-4">
                        {menu.links.map((sub, idx) => (
                          <a
                            key={idx}
                            href={sub.path}
                            onClick={handleLinkClick}
                            className="block py-2 text-sm text-slate-500 hover:text-primary hover:translate-x-1 transition-all"
                          >
                            <span className="block font-medium">
                              {sub.title}
                            </span>
                            {sub.description && (
                              <span className="text-[10px] text-slate-400 line-clamp-1">
                                {sub.description}
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <a
                    href={menu.href}
                    onClick={handleLinkClick}
                    className={classNames(
                      "w-full flex items-center gap-4 py-4 px-3 rounded-lg text-lg font-medium transition-colors group",
                    )}
                  >
                    <span className="w-8 flex justify-center text-primary group-hover:scale-110 transition-transform">
                      <FontAwesomeIcon icon={menu.icon} />
                    </span>
                    <span>{menu.title}</span>
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-primary font-bold mb-4 uppercase text-xs tracking-[0.3em]">
              Contact Us
            </h3>
            <div className="gap-4 space-y-2">
              <a
                href={`tel:${GlobalData.company.companyPhone}`}
                className="flex items-center gap-4 text-slate-600 hover:text-primary transition-colors font-medium"
                onClick={handleLinkClick}
              >
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white">
                  <FontAwesomeIcon icon={faPhone} size="sm" />
                </div>
                <span>{GlobalData.company.companyPhone}</span>
              </a>
              <a
                href={`mailto:${GlobalData.company.companyEmail}`}
                className="flex items-center gap-4 text-slate-600 hover:text-primary transition-colors font-medium"
                onClick={handleLinkClick}
              >
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white">
                  <FontAwesomeIcon icon={faEnvelope} size="sm" />
                </div>
                <span>{GlobalData.company.companyEmail}</span>
              </a>
            </div>


          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[4500]"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar;
